import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, Building, MapPin } from 'lucide-react';
import { GIORNI, MESI, formatDateLocal } from '../../lib/calendarioUtils';

/** Viste calendario (mese/settimana/giorno). Presentazionale: stato e handler nel parent. */
export default function CalendarioViste({
  vista, dataCorrente, giorniMese, giorniSettimana,
  getAppuntamentiGiorno, onNuovo, onDettaglio,
}) {
  const isOggi = (data) => data.toDateString() === new Date().toDateString();
  const isMeseCorrente = (data) => data.getMonth() === dataCorrente.getMonth();

  if (vista === 'mese') {
    return (
      <Card className="rounded-none overflow-hidden">
        <div className="grid grid-cols-7">
          {GIORNI.map((g) => (
            <div key={g} className="p-2 text-center text-xs font-mono uppercase bg-muted border-b">
              {g}
            </div>
          ))}
          {giorniMese.map((giorno) => {
            const appGiorno = getAppuntamentiGiorno(giorno);
            return (
              <div
                key={formatDateLocal(giorno)}
                className={`min-h-[100px] border p-1 cursor-pointer transition-colors hover:bg-muted/50 ${
                  !isMeseCorrente(giorno) ? 'bg-muted/30 text-muted-foreground' : ''
                } ${isOggi(giorno) ? 'bg-cyan-50 dark:bg-cyan-950' : ''}`}
                onClick={() => onNuovo(formatDateLocal(giorno))}
                data-testid={`giorno-${formatDateLocal(giorno)}`}
              >
                <div className={`text-sm font-mono ${isOggi(giorno) ? 'text-cyan-600 font-bold' : ''}`}>
                  {giorno.getDate()}
                </div>
                <div className="space-y-1 mt-1">
                  {appGiorno.slice(0, 3).map((app) => (
                    <div
                      key={app.id}
                      className="text-xs p-1 truncate cursor-pointer hover:opacity-80"
                      style={{
                        backgroundColor: `${app.tecnico_colore}20`,
                        borderLeft: `3px solid ${app.tecnico_colore}`,
                        color: app.tecnico_colore
                      }}
                      onClick={(e) => { e.stopPropagation(); onDettaglio(app); }}
                    >
                      <span className="font-medium">{app.ora_inizio}</span> {app.titolo}
                    </div>
                  ))}
                  {appGiorno.length > 3 && (
                    <div className="text-xs text-muted-foreground text-center">
                      +{appGiorno.length - 3} altri
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  if (vista === 'settimana') {
    return (
      <Card className="rounded-none overflow-x-auto">
        <div className="grid grid-cols-7 min-w-[800px]">
          {giorniSettimana.map((giorno, idx) => (
            <div key={formatDateLocal(giorno)} className="border">
              <div className={`p-2 text-center border-b ${isOggi(giorno) ? 'bg-cyan-100 dark:bg-cyan-900' : 'bg-muted'}`}>
                <div className="text-xs font-mono uppercase">{GIORNI[idx]}</div>
                <div className={`text-2xl font-bold ${isOggi(giorno) ? 'text-cyan-600' : ''}`}>
                  {giorno.getDate()}
                </div>
              </div>
              <div
                className="min-h-[400px] p-2 space-y-2 cursor-pointer hover:bg-muted/30"
                onClick={() => onNuovo(formatDateLocal(giorno))}
              >
                {getAppuntamentiGiorno(giorno).map((app) => (
                  <div
                    key={app.id}
                    className="p-2 text-sm cursor-pointer hover:opacity-80"
                    style={{
                      backgroundColor: `${app.tecnico_colore}20`,
                      borderLeft: `4px solid ${app.tecnico_colore}`
                    }}
                    onClick={(e) => { e.stopPropagation(); onDettaglio(app); }}
                  >
                    <div className="font-bold" style={{ color: app.tecnico_colore }}>
                      {app.ora_inizio} - {app.ora_fine || '??'}
                    </div>
                    <div className="font-medium truncate">{app.titolo}</div>
                    <div className="text-xs text-muted-foreground truncate">{app.cliente_nome}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  // Vista giorno
  return (
    <Card className="rounded-none">
      <CardHeader className={isOggi(dataCorrente) ? 'bg-cyan-50 dark:bg-cyan-950' : ''}>
        <CardTitle className="text-lg">
          {GIORNI[(dataCorrente.getDay() + 6) % 7]} {dataCorrente.getDate()} {MESI[dataCorrente.getMonth()]}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {getAppuntamentiGiorno(dataCorrente).length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            Nessun appuntamento per questa giornata
          </div>
        ) : (
          getAppuntamentiGiorno(dataCorrente).map((app) => (
            <div
              key={app.id}
              className="p-4 border cursor-pointer hover:bg-muted/50 transition-colors"
              style={{ borderLeftWidth: 4, borderLeftColor: app.tecnico_colore }}
              onClick={() => onDettaglio(app)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" style={{ color: app.tecnico_colore }} />
                    <span className="font-bold" style={{ color: app.tecnico_colore }}>
                      {app.ora_inizio} - {app.ora_fine || '??'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-lg mt-1">{app.titolo}</h3>
                  {app.cliente_nome && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                      <Building className="h-4 w-4" />
                      {app.cliente_nome}
                    </div>
                  )}
                  {app.luogo && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      {app.luogo}
                    </div>
                  )}
                </div>
                <Badge style={{ backgroundColor: app.tecnico_colore }} className="rounded-none">
                  {app.tecnico_nome}
                </Badge>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
